import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, X, CheckCircle, XCircle } from "lucide-react";
import useModal from "hooks/use-modal";
import axios from "axios";

const ModalTermsScroller = (props) => {
  const { title, content, accept, reject, fetchUrl } = props.modalProps;
  const { closeModal } = useModal();
  const [scrolled, setScrolled] = useState(false);
  const [htmlContent, setHtmlContent] = useState(content);

  // Animation variants
  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  const handleScroll = (e) => {
    const target = e.target;
    const scrollPo = target.scrollHeight - target.scrollTop;
    const scrollTarget = target.clientHeight / scrollPo;
    if (scrollTarget > 0.98 && !scrolled) {
      setScrolled(true);
    }
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(fetchUrl);
        setHtmlContent(response.data);
      } catch (e) {}
    };
    if (fetchUrl) {
      fetchContent();
    }
  }, [fetchUrl]);

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Modal Header */}
      <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={reject || closeModal}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
              {title || "Terms & Conditions"}
            </h2>
            <p className="text-blue-100 text-sm sm:text-base">
              Please review and accept the terms below
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-hidden flex-1 min-h-0 bg-gray-900 flex flex-col"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        {/* Scrollable Content Area */}
        <motion.div
          variants={itemAnimation}
          className="mb-6"
          style={{ maxHeight: "400px" }}
        >
          <div
            className="h-full max-h-[400px] overflow-y-auto bg-white border border-gray-200 rounded-xl p-6 text-gray-900 scrollBox-modern"
            onScroll={handleScroll}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </motion.div>

        {/* Action Buttons */}
        {accept !== undefined && (
          <motion.div
            variants={itemAnimation}
            className="flex flex-col sm:flex-row gap-3 justify-center"
          >
            <button
              onClick={reject}
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
            >
              <XCircle className="w-4 h-4" />I do not accept
            </button>
            <button
              onClick={accept}
              disabled={!scrolled}
              className={`px-6 py-3 rounded-xl transition-all font-medium shadow-lg flex items-center justify-center gap-2 ${
                scrolled
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white hover:shadow-xl"
                  : "bg-gray-700/50 text-gray-400 cursor-not-allowed"
              }`}
            >
              <CheckCircle className="w-4 h-4" />I have read and accept the
              above
            </button>
          </motion.div>
        )}

        {/* Scroll Indicator */}
        {!scrolled && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-gray-400 text-sm mt-2"
          >
            Please scroll to the bottom to continue
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default ModalTermsScroller;
